import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Container } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFlask, faCloudArrowUp, faFileLines, faChartLine,
  faFilePdf, faSpinner, faCircleCheck, faCircleXmark,
  faArrowLeft, faDownload, faTrash, faEye,
  faCalendarDays, faUser, faHospital, faStethoscope,
  faHeartPulse, faAppleWhole, faPersonRunning, faUserDoctor,
  faVial, faClipboardList, faTriangleExclamation,
  faCircleExclamation, faCheckCircle, faExclamationTriangle,
  faArrowUp, faArrowDown, faMinus, faWallet,
  faPlus, faIndianRupeeSign, faTimes, faBolt, faGem, faCrown
} from '@fortawesome/free-solid-svg-icons';
import BackButton from '../../components/BackButton/BackButton';
import smartReportsService from '../../services/smartReportsService';
import {
  SmartReport, SmartReportListItem, JobStatus,
  TestResult, HealthCategory, DietRecommendation,
  LifestyleRecommendation, SpecialistReferral,
  WalletBalance, SRCreateOrderResponse
} from '../../types/smartReports';
import './SmartReports.css';
import { useProfile } from '../../context/ProfileContext';
import { useAuth } from '../../context/AuthContext';

type TabKey = 'reports' | 'upload' | 'view';

const TAB_CONFIG: { key: TabKey; label: string; icon: typeof faFlask }[] = [
  { key: 'reports', label: 'My Reports', icon: faClipboardList },
  { key: 'upload', label: 'Upload Report', icon: faCloudArrowUp },
  { key: 'view', label: 'Report View', icon: faFileLines },
];

const PROCESSING_STEPS = [
  { key: 'pending', label: 'Upload Received', desc: 'Your report has been received' },
  { key: 'extracting', label: 'Extracting Text', desc: 'Reading data from your PDF' },
  { key: 'analyzing', label: 'Analyzing Results', desc: 'AI is analyzing your blood work' },
  { key: 'generating', label: 'Generating Report', desc: 'Creating your personalized report' },
  { key: 'completed', label: 'Complete', desc: 'Your report is ready!' },
];

const getScoreColor = (score: number): string => {
  if (score >= 80) return '#10b981';
  if (score >= 60) return '#f59e0b';
  return '#ef4444';
};

const formatDate = (dateStr: string): string => {
  if (!dateStr) return '--';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const CREDIT_PACKS = [
  { amount: 99, credits: 1, label: 'Starter', icon: faBolt, color: '#06b6d4', tag: '' },
  { amount: 249, credits: 3, label: 'Value', icon: faGem, color: '#8b5cf6', tag: 'Save 16%' },
  { amount: 449, credits: 6, label: 'Pro', icon: faCrown, color: '#f59e0b', tag: 'Best Value' },
];

const SmartReports: React.FC = () => {
  const { t } = useTranslation(['services', 'common']);
  const { activeProfile } = useProfile();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as TabKey | null;

  const [activeTab, setActiveTab] = useState<TabKey>(tabParam === 'upload' ? 'upload' : 'reports');
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);

  // In-app toast notification (replaces browser alert)
  const [toastMsg, setToastMsg] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);
  const showToast = (message: string, type: 'success' | 'error' | 'warning' = 'error') => {
    setToastMsg({ message, type });
    setTimeout(() => setToastMsg(null), 4000);
  };

  // Reports list
  const [reports, setReports] = useState<SmartReportListItem[]>([]);
  const [totalReports, setTotalReports] = useState(0);

  // Stats
  const [walletBalance, setWalletBalance] = useState<WalletBalance | null>(null);

  // Upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [processingJobId, setProcessingJobId] = useState<string | null>(null);
  const [processingStatus, setProcessingStatus] = useState<string>('pending');
  const [processingError, setProcessingError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // Report view
  const [viewingReport, setViewingReport] = useState<SmartReport | null>(null);
  const [reportLoading, setReportLoading] = useState(false);

  // Add credits modal
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [selectedPack, setSelectedPack] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [payuData, setPayuData] = useState<SRCreateOrderResponse | null>(null);
  const [payuBaseUrl, setPayuBaseUrl] = useState('https://secure.payu.in/_payment');
  const payuFormRef = useRef<HTMLFormElement>(null);
  const [paymentBanner, setPaymentBanner] = useState<{ tone: 'success' | 'error' | 'info'; message: string } | null>(null);

  // ─── Initialize auth + load data ───────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const ok = await smartReportsService.ensureAuth();
        setAuthReady(ok);
        if (ok) {
          await loadReports();
          loadWallet();
        }
      } catch (err) {
        console.error('Smart Reports init error:', err);
      } finally {
        setLoading(false);
      }
    };
    init();

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProfile]);

  const loadReports = async () => {
    try {
      const data = await smartReportsService.getMyReports(1, 50);
      setReports(data.reports);
      setTotalReports(data.total);
    } catch (err) {
      console.error('Error loading reports:', err);
    }
  };

  const loadWallet = async () => {
    try {
      const data = await smartReportsService.getWalletBalance();
      setWalletBalance(data);
    } catch (err) {
      console.error('Error loading wallet:', err);
    }
  };

  // ─── Tab change ────────────────────────────────────────────────────────
  const handleTabChange = (tab: TabKey) => {
    setActiveTab(tab);
    setSearchParams(tab === 'reports' ? {} : { tab });
  };

  // ─── File selection ────────────────────────────────────────────────────
  const handleFileSelect = (file: File) => {
    if (file.type !== 'application/pdf') {
      showToast(t('services:smartReports.selectPdfFile'), 'warning');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      showToast(t('services:smartReports.fileSizeLimit'), 'warning');
      return;
    }
    setSelectedFile(file);
    setProcessingError(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  // ─── Upload + poll ─────────────────────────────────────────────────────
  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setProcessingError(null);
    setProcessingStatus('pending');

    try {
      const result = await smartReportsService.uploadReport(selectedFile);
      setProcessingJobId(result.job_id);
      setUploading(false);
      startPolling(result.job_id);
    } catch (err: any) {
      setUploading(false);
      const detail = err.response?.data?.detail;
      if (detail?.message === 'Insufficient credits') {
        setProcessingError(`Insufficient credits. Balance: ${detail.balance}, Required: ${detail.required}`);
      } else {
        setProcessingError(err.response?.data?.detail || err.message || 'Upload failed');
      }
    }
  };

  const startPolling = (jobId: string) => {
    if (pollingRef.current) clearInterval(pollingRef.current);

    pollingRef.current = setInterval(async () => {
      try {
        const status: JobStatus = await smartReportsService.getJobStatus(jobId);
        setProcessingStatus(status.status);

        if (status.status === 'completed') {
          if (pollingRef.current) clearInterval(pollingRef.current);
          pollingRef.current = null;
          // Load the completed report
          await viewReport(jobId);
          await loadReports();
          loadWallet();
          setSelectedFile(null);
          setProcessingJobId(null);
        } else if (status.status === 'failed') {
          if (pollingRef.current) clearInterval(pollingRef.current);
          pollingRef.current = null;
          setProcessingError(status.error_message || 'Analysis failed. Please try again.');
          setProcessingJobId(null);
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 3000);
  };

  // ─── View report ───────────────────────────────────────────────────────
  const viewReport = useCallback(async (jobId: string) => {
    setReportLoading(true);
    try {
      const report = await smartReportsService.getReport(jobId);
      setViewingReport(report);
      setActiveTab('view');
      setSearchParams({ tab: 'view' });
    } catch (err) {
      console.error('Error loading report:', err);
      showToast(t('services:smartReports.failedToLoadReport'));
    } finally {
      setReportLoading(false);
    }
  }, [setSearchParams]);

  // ─── Download PDF ──────────────────────────────────────────────────────
  const handleDownloadPdf = async (jobId: string) => {
    try {
      await smartReportsService.downloadReportPdf(jobId);
    } catch (err) {
      console.error('Download error:', err);
      showToast(t('services:smartReports.failedToDownloadPdf'));
    }
  };

  // ─── Load PayU Config ────────────────────────────────────────────────
  useEffect(() => {
    smartReportsService.getPaymentConfig()
      .then((config) => {
        if (config.payu_base_url) setPayuBaseUrl(config.payu_base_url + '/_payment');
      })
      .catch(() => {}); // fallback to default
  }, []);

  // ─── Handle PayU redirect back to this page (?payment=success|failed|cancelled)
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    if (!paymentStatus) return;
    if (paymentStatus === 'success') {
      setPaymentBanner({ tone: 'success', message: 'Payment successful. Your Smart Report credits have been added.' });
      loadWallet();
    } else if (paymentStatus === 'cancelled') {
      setPaymentBanner({ tone: 'info', message: 'Payment was cancelled. No credits were added.' });
    } else {
      setPaymentBanner({ tone: 'error', message: 'Payment failed. Please try again to purchase credits.' });
    }
    const next = new URLSearchParams(searchParams);
    next.delete('payment');
    next.delete('txn_id');
    setSearchParams(next, { replace: true });
    const dismiss = window.setTimeout(() => setPaymentBanner(null), 8000);
    return () => window.clearTimeout(dismiss);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // ─── Add Credits ─────────────────────────────────────────────────────
  const handleAddCredits = async (amount: number) => {
    if (amount <= 0) return;
    setPaymentLoading(true);
    setPaymentError(null);
    try {
      const orderData = await smartReportsService.createPaymentOrder(
        amount,
        user?.name || '',
        user?.email || '',
        user?.mobile_number || ''
      );
      // Override PayU return URLs so the browser lands back on the SPA after
      // PayU redirects — otherwise a cancellation strands the user on PayU or
      // on the external SR service's raw JSON response. surl/furl are not
      // part of the PayU checksum, so this rewrite is safe.
      const apiBase = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
      const returnUrl = `${apiBase}/api/payments/sr-return/`;
      setPayuData({ ...orderData, surl: returnUrl, furl: returnUrl });
      setTimeout(() => {
        payuFormRef.current?.submit();
      }, 100);
    } catch (err: any) {
      setPaymentError(err.response?.data?.detail || err.message || 'Payment initiation failed');
      setPaymentLoading(false);
    }
  };

  const handlePackSelect = (idx: number) => {
    setSelectedPack(idx);
    setCustomAmount('');
    setPaymentError(null);
  };

  const handleProceedPayment = () => {
    if (selectedPack !== null) {
      handleAddCredits(CREDIT_PACKS[selectedPack].amount);
    } else if (customAmount && Number(customAmount) >= 49) {
      handleAddCredits(Number(customAmount));
    }
  };

  // ─── Stats computed ────────────────────────────────────────────────────
  const completedReports = reports.filter(r => r.status === 'completed');
  const lastReportDate = completedReports.length > 0
    ? formatDate(completedReports[0].created_at)
    : '--';

  // ─── Render helpers ────────────────────────────────────────────────────

  const getStepState = (stepKey: string): 'completed' | 'active' | 'pending' | 'failed' => {
    if (processingError && stepKey === processingStatus) return 'failed';
    const stepOrder = PROCESSING_STEPS.map(s => s.key);
    const currentIdx = stepOrder.indexOf(processingStatus);
    const stepIdx = stepOrder.indexOf(stepKey);
    if (stepIdx < currentIdx) return 'completed';
    if (stepIdx === currentIdx) return processingStatus === 'completed' ? 'completed' : 'active';
    return 'pending';
  };

  const renderScoreCircle = (score: number, size: number = 58) => {
    const radius = (size - 8) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (score / 100) * circumference;
    const color = getScoreColor(score);

    return (
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke="#f1f5f9" strokeWidth="6" />
        <circle cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke={color} strokeWidth="6"
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" />
      </svg>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════

  if (loading) {
    return (
      <div className="sr-page">
        <section className="sr-welcome-banner">
          <Container>
            <BackButton />
            <div className="sr-welcome-layout">
              <div className="sr-welcome-text">
                <h1 className="sr-welcome-title">
                  <FontAwesomeIcon icon={faFlask} /> {t('services:smartReports.title')}
                </h1>
                <p className="sr-welcome-subtitle">{t('services:smartReports.loadingReports')}</p>
              </div>
            </div>
          </Container>
        </section>
        <Container>
          <div className="sr-loading-container">
            <FontAwesomeIcon icon={faSpinner} className="sr-loading-spinner" />
            <span className="sr-loading-text">{t('services:smartReports.connectingToSR')}</span>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="sr-page">

      {/* ═══ WELCOME BANNER ═══ */}
      <section className="sr-welcome-banner">
        <Container>
          <BackButton />
          <div className="sr-welcome-layout">
            <div className="sr-welcome-text">
              <h1 className="sr-welcome-title">
                <FontAwesomeIcon icon={faFlask} /> {t('services:smartReports.title')}
              </h1>
              <p className="sr-welcome-subtitle">
                {t('services:smartReports.subtitle')}
              </p>
            </div>
            <div className="sr-welcome-actions">
              <button className="sr-welcome-btn primary" onClick={() => handleTabChange('upload')}>
                <FontAwesomeIcon icon={faCloudArrowUp} /> {t('services:smartReports.uploadReport')}
              </button>
              <button className="sr-welcome-btn secondary" onClick={() => handleTabChange('reports')}>
                <FontAwesomeIcon icon={faClipboardList} /> {t('services:smartReports.myReports')}
              </button>
            </div>
          </div>
        </Container>
      </section>

      {/* Payment result banner after PayU redirect */}
      {paymentBanner && (
        <Container>
          <div
            role="status"
            style={{
              margin: '16px 0',
              padding: '12px 16px',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              background:
                paymentBanner.tone === 'success'
                  ? '#ecfdf5'
                  : paymentBanner.tone === 'error'
                  ? '#fef2f2'
                  : '#eff6ff',
              color:
                paymentBanner.tone === 'success'
                  ? '#065f46'
                  : paymentBanner.tone === 'error'
                  ? '#991b1b'
                  : '#1e3a8a',
              border: `1px solid ${
                paymentBanner.tone === 'success'
                  ? '#a7f3d0'
                  : paymentBanner.tone === 'error'
                  ? '#fecaca'
                  : '#bfdbfe'
              }`,
            }}
          >
            <FontAwesomeIcon
              icon={
                paymentBanner.tone === 'success'
                  ? faCircleCheck
                  : paymentBanner.tone === 'error'
                  ? faCircleXmark
                  : faCircleExclamation
              }
            />
            <span>{paymentBanner.message}</span>
          </div>
        </Container>
      )}

      {/* ═══ STATS ═══ */}
      <Container>
        <section className="sr-stats-section">
          <div className="sr-stats-row">
            <div className="sr-stat-card">
              <div className="sr-stat-icon" style={{ background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}>
                <FontAwesomeIcon icon={faFileLines} />
              </div>
              <div className="sr-stat-info">
                <span className="sr-stat-value">{totalReports}</span>
                <span className="sr-stat-label">{t('services:smartReports.totalReports')}</span>
              </div>
            </div>
            <div className="sr-stat-card">
              <div className="sr-stat-icon" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
                <FontAwesomeIcon icon={faChartLine} />
              </div>
              <div className="sr-stat-info">
                <span className="sr-stat-value">{completedReports.length}</span>
                <span className="sr-stat-label">{t('services:smartReports.completed')}</span>
              </div>
            </div>
            <div className="sr-stat-card sr-stat-card-wallet" onClick={() => setShowCreditsModal(true)}>
              <div className="sr-stat-icon" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>
                <FontAwesomeIcon icon={faWallet} />
              </div>
              <div className="sr-stat-info">
                <span className="sr-stat-value">{walletBalance ? walletBalance.balance.toFixed(1) : '--'}</span>
                <span className="sr-stat-label">{t('services:smartReports.creditsBalance')}</span>
              </div>
              <button className="sr-add-credits-btn" onClick={(e) => { e.stopPropagation(); setShowCreditsModal(true); }}>
                <FontAwesomeIcon icon={faPlus} /> {t('services:smartReports.addCredits')}
              </button>
            </div>
            <div className="sr-stat-card">
              <div className="sr-stat-icon" style={{ background: 'rgba(6, 182, 212, 0.1)', color: '#06b6d4' }}>
                <FontAwesomeIcon icon={faCalendarDays} />
              </div>
              <div className="sr-stat-info">
                <span className="sr-stat-value">{lastReportDate}</span>
                <span className="sr-stat-label">{t('services:smartReports.lastReport')}</span>
              </div>
            </div>
          </div>
        </section>
      </Container>

      {/* ═══ TABS ═══ */}
      <Container>
        <div className="sr-tabs-container">
          {TAB_CONFIG.map(tab => (
            <button
              key={tab.key}
              className={`sr-tab-btn ${activeTab === tab.key ? 'active' : ''}`}
              data-tab={tab.key}
              onClick={() => handleTabChange(tab.key)}
            >
              <FontAwesomeIcon icon={tab.icon} className="sr-tab-icon" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* ═══ CONTENT ═══ */}
        <div className="sr-content">

          {/* ─── My Reports Tab ─── */}
          {activeTab === 'reports' && (
            <>
              {reports.length === 0 ? (
                <div className="sr-empty-state">
                  <FontAwesomeIcon icon={faFileLines} className="sr-empty-icon" />
                  <div className="sr-empty-title">{t('services:smartReports.noReportsYet')}</div>
                  <div className="sr-empty-subtitle">
                    {t('services:smartReports.noReportsDesc')}
                  </div>
                  <button className="sr-empty-btn" onClick={() => handleTabChange('upload')}>
                    <FontAwesomeIcon icon={faCloudArrowUp} /> {t('services:smartReports.uploadReport')}
                  </button>
                </div>
              ) : (
                <div className="sr-reports-grid">
                  {reports.map((report, idx) => (
                    <div
                      key={report.id}
                      className="sr-report-card"
                      style={{ animationDelay: `${idx * 0.05}s` }}
                      onClick={() => report.status === 'completed' && viewReport(report.job_id)}
                    >
                      <div className="sr-report-card-header">
                        <div className="sr-report-card-title">
                          <FontAwesomeIcon icon={faFilePdf} />
                          {report.filename || `Report ${report.job_id}`}
                        </div>
                        <span className={`sr-report-status-pill ${report.status}`}>
                          {report.status === 'completed' && <FontAwesomeIcon icon={faCircleCheck} />}
                          {(report.status === 'processing' || report.status === 'pending') && <FontAwesomeIcon icon={faSpinner} spin />}
                          {report.status === 'failed' && <FontAwesomeIcon icon={faCircleXmark} />}
                          {report.status}
                        </span>
                      </div>
                      <div className="sr-report-card-body">
                        <div className="sr-report-meta">
                          <div className="sr-report-meta-row">
                            <FontAwesomeIcon icon={faCalendarDays} />
                            <span>Uploaded: {formatDate(report.created_at)}</span>
                          </div>
                          {report.completed_at && (
                            <div className="sr-report-meta-row">
                              <FontAwesomeIcon icon={faCircleCheck} />
                              <span>Completed: {formatDate(report.completed_at)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      {report.status === 'completed' && (
                        <div className="sr-report-card-actions">
                          <button className="sr-report-action-btn primary" onClick={(e) => { e.stopPropagation(); viewReport(report.job_id); }}>
                            <FontAwesomeIcon icon={faEye} /> View Report
                          </button>
                          <button className="sr-report-action-btn outline" onClick={(e) => { e.stopPropagation(); handleDownloadPdf(report.job_id); }}>
                            <FontAwesomeIcon icon={faDownload} /> PDF
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ─── Upload Tab ─── */}
          {activeTab === 'upload' && (
            <>
              {processingJobId ? (
                /* Processing stepper */
                <div className="sr-processing-card">
                  <div className="sr-processing-title">Analyzing Your Report</div>
                  <div className="sr-stepper">
                    {PROCESSING_STEPS.map((step, idx) => {
                      const state = getStepState(step.key);
                      return (
                        <div key={step.key} className={`sr-step ${state}`}>
                          <div className={`sr-step-indicator ${state}`}>
                            {state === 'completed' && <FontAwesomeIcon icon={faCircleCheck} />}
                            {state === 'active' && <FontAwesomeIcon icon={faSpinner} spin />}
                            {state === 'failed' && <FontAwesomeIcon icon={faCircleXmark} />}
                            {state === 'pending' && <span>{idx + 1}</span>}
                          </div>
                          {idx < PROCESSING_STEPS.length - 1 && (
                            <div className={`sr-step-line ${state === 'completed' ? 'completed' : ''}`} />
                          )}
                          <div className="sr-step-content">
                            <div className="sr-step-title">{step.label}</div>
                            <div className="sr-step-desc">{step.desc}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : processingError ? (
                /* Error state */
                <div className="sr-error-card">
                  <FontAwesomeIcon icon={faCircleExclamation} className="sr-error-icon" />
                  <div className="sr-error-title">Analysis Failed</div>
                  <div className="sr-error-msg">{processingError}</div>
                  <div className="sr-error-actions">
                    <button className="sr-upload-btn" onClick={() => { setProcessingError(null); setSelectedFile(null); }}>
                      <FontAwesomeIcon icon={faCloudArrowUp} /> Try Again
                    </button>
                  </div>
                </div>
              ) : (
                /* Upload form */
                <div className="sr-upload-container">
                  <div className="sr-upload-card">
                    <div className="sr-upload-title">Upload Blood Report</div>
                    <div className="sr-upload-subtitle">
                      Upload a PDF of your blood test report. Our AI will analyze the results and provide
                      personalized health insights, diet recommendations, and follow-up suggestions.
                    </div>

                    <div
                      className={`sr-dropzone ${isDragOver ? 'dragover' : ''}`}
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <FontAwesomeIcon icon={faCloudArrowUp} className="sr-dropzone-icon" />
                      <div className="sr-dropzone-text">Drop your PDF here or click to browse</div>
                      <div className="sr-dropzone-hint">PDF files only, up to 20MB</div>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,application/pdf"
                      style={{ display: 'none' }}
                      onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                    />

                    {selectedFile && (
                      <div className="sr-file-preview">
                        <div className="sr-file-icon">
                          <FontAwesomeIcon icon={faFilePdf} />
                        </div>
                        <div className="sr-file-info">
                          <div className="sr-file-name">{selectedFile.name}</div>
                          <div className="sr-file-size">{formatFileSize(selectedFile.size)}</div>
                        </div>
                        <button className="sr-file-remove" onClick={() => setSelectedFile(null)}>
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    )}

                    {selectedFile && (
                      <div className="sr-upload-actions">
                        <button
                          className="sr-upload-btn"
                          disabled={uploading}
                          onClick={handleUpload}
                        >
                          {uploading ? (
                            <><FontAwesomeIcon icon={faSpinner} spin /> Uploading...</>
                          ) : (
                            <><FontAwesomeIcon icon={faFlask} /> Analyze Report</>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ─── Report View Tab ─── */}
          {activeTab === 'view' && (
            <>
              {reportLoading ? (
                <div className="sr-loading-container">
                  <FontAwesomeIcon icon={faSpinner} className="sr-loading-spinner" />
                  <span className="sr-loading-text">Loading report...</span>
                </div>
              ) : !viewingReport ? (
                <div className="sr-empty-state">
                  <FontAwesomeIcon icon={faFileLines} className="sr-empty-icon" />
                  <div className="sr-empty-title">No Report Selected</div>
                  <div className="sr-empty-subtitle">
                    Select a report from "My Reports" or upload a new one to view the analysis.
                  </div>
                  <button className="sr-empty-btn" onClick={() => handleTabChange('reports')}>
                    <FontAwesomeIcon icon={faClipboardList} /> Go to My Reports
                  </button>
                </div>
              ) : (
                <>
                  {/* Report Header */}
                  <div className="sr-report-header">
                    <div className="sr-report-header-left">
                      <button className="sr-report-back-btn" onClick={() => handleTabChange('reports')}>
                        <FontAwesomeIcon icon={faArrowLeft} /> Back to Reports
                      </button>
                      <span className="sr-report-header-title">
                        {viewingReport.patient_info?.name || 'Report'} — {formatDate(viewingReport.patient_info?.date_of_test || viewingReport.created_at)}
                      </span>
                    </div>
                    <div className="sr-report-header-actions">
                      <button className="sr-download-btn" onClick={() => handleDownloadPdf(viewingReport.job_id)}>
                        <FontAwesomeIcon icon={faDownload} /> Download PDF
                      </button>
                    </div>
                  </div>

                  {/* Section 1: Document Overview */}
                  {viewingReport.document_summary && (
                    <div className="sr-section-card">
                      <div className="sr-section-header">
                        <div className="sr-section-icon" style={{ background: 'rgba(6, 182, 212, 0.1)', color: '#06b6d4' }}>
                          <FontAwesomeIcon icon={faFileLines} />
                        </div>
                        <span className="sr-section-title">Document Overview</span>
                      </div>
                      <div className="sr-section-body">
                        <div className="sr-doc-overview-grid">
                          {viewingReport.patient_info && (
                            <>
                              <div className="sr-doc-info-item">
                                <span className="sr-doc-info-label">Patient</span>
                                <span className="sr-doc-info-value">{viewingReport.patient_info.name}</span>
                              </div>
                              <div className="sr-doc-info-item">
                                <span className="sr-doc-info-label">Age / Gender</span>
                                <span className="sr-doc-info-value">{viewingReport.patient_info.age} / {viewingReport.patient_info.gender}</span>
                              </div>
                              <div className="sr-doc-info-item">
                                <span className="sr-doc-info-label">Lab</span>
                                <span className="sr-doc-info-value">{viewingReport.patient_info.lab_name}</span>
                              </div>
                              <div className="sr-doc-info-item">
                                <span className="sr-doc-info-label">Test Date</span>
                                <span className="sr-doc-info-value">{formatDate(viewingReport.patient_info.date_of_test)}</span>
                              </div>
                            </>
                          )}
                          <div className="sr-doc-info-item">
                            <span className="sr-doc-info-label">Document Type</span>
                            <span className="sr-doc-info-value">{viewingReport.document_summary.document_type}</span>
                          </div>
                        </div>
                        {viewingReport.document_summary.key_findings.length > 0 && (
                          <div className="sr-doc-findings">
                            <div className="sr-doc-findings-title">Key Findings</div>
                            <div className="sr-doc-finding-tags">
                              {viewingReport.document_summary.key_findings.map((f, i) => (
                                <span key={i} className="sr-doc-finding-tag">
                                  <FontAwesomeIcon icon={faTriangleExclamation} /> {f}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="sr-doc-urgency">
                          <span className={`sr-urgency-badge ${viewingReport.document_summary.urgency_level === 'urgent' ? 'urgent' : viewingReport.document_summary.urgency_level === 'attention-needed' ? 'soon' : 'routine'}`}>
                            {viewingReport.document_summary.urgency_level}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Section 2: Health Score */}
                  {viewingReport.overall_health_score != null && (
                    <div className="sr-section-card">
                      <div className="sr-section-header">
                        <div className="sr-section-icon" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
                          <FontAwesomeIcon icon={faHeartPulse} />
                        </div>
                        <span className="sr-section-title">Health Score</span>
                      </div>
                      <div className="sr-section-body">
                        <div className="sr-score-section">
                          <div className="sr-score-circle-large">
                            {renderScoreCircle(viewingReport.overall_health_score, 140)}
                            <div className="score-value">
                              <div className="score-number">{viewingReport.overall_health_score}</div>
                              <div className="score-label">out of 100</div>
                            </div>
                          </div>
                          <div className="sr-score-details">
                            {viewingReport.personalized_summary && (
                              <>
                                <div className="sr-score-assessment">
                                  {viewingReport.personalized_summary.overall_assessment}
                                </div>
                                <div className="sr-score-findings">
                                  {viewingReport.personalized_summary.positive_findings.map((f, i) => (
                                    <div key={`pos-${i}`} className="sr-score-finding positive">
                                      <FontAwesomeIcon icon={faCheckCircle} /> {f}
                                    </div>
                                  ))}
                                  {viewingReport.personalized_summary.key_concerns.map((c, i) => (
                                    <div key={`con-${i}`} className="sr-score-finding concern">
                                      <FontAwesomeIcon icon={faExclamationTriangle} /> {c}
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Section 3: Test Results */}
                  {viewingReport.all_tests.length > 0 && (
                    <div className="sr-section-card">
                      <div className="sr-section-header">
                        <div className="sr-section-icon" style={{ background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}>
                          <FontAwesomeIcon icon={faVial} />
                        </div>
                        <span className="sr-section-title">Test Results ({viewingReport.all_tests.length})</span>
                      </div>
                      <div className="sr-section-body">
                        <div className="sr-tests-table-wrap">
                          <table className="sr-tests-table">
                            <thead>
                              <tr>
                                <th>Test Name</th>
                                <th>Value</th>
                                <th>Reference Range</th>
                                <th>Status</th>
                                <th>Category</th>
                              </tr>
                            </thead>
                            <tbody>
                              {viewingReport.all_tests.map((test, i) => (
                                <tr key={i}>
                                  <td>
                                    <strong>{test.test_name}</strong>
                                    {test.insight && (
                                      <div className="sr-test-insight">{test.insight.your_status}</div>
                                    )}
                                  </td>
                                  <td>{test.value} {test.unit}</td>
                                  <td>{test.reference_range}</td>
                                  <td>
                                    <span className={`sr-test-status-pill ${test.status === 'Very Abnormal' ? 'VeryAbnormal' : test.status}`}>
                                      {test.status}
                                    </span>
                                  </td>
                                  <td>{test.category}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Section 4: Health Categories */}
                  {viewingReport.health_summary.length > 0 && (
                    <div className="sr-section-card">
                      <div className="sr-section-header">
                        <div className="sr-section-icon" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>
                          <FontAwesomeIcon icon={faChartLine} />
                        </div>
                        <span className="sr-section-title">Health Categories</span>
                      </div>
                      <div className="sr-section-body">
                        <div className="sr-categories-grid">
                          {viewingReport.health_summary.map((cat, i) => {
                            const normalCount = cat.parameters.filter(p => p.status === 'Normal').length;
                            const totalCount = cat.parameters.length;
                            const pct = totalCount > 0 ? (normalCount / totalCount) * 100 : 0;
                            const barColor = cat.overall_status === 'Normal' ? '#10b981'
                              : cat.overall_status === 'Borderline' ? '#f59e0b' : '#ef4444';

                            return (
                              <div key={i} className="sr-category-card">
                                <div className="sr-category-header">
                                  <span className="sr-category-name">{cat.category}</span>
                                  <span className="sr-category-status" style={{
                                    background: `${barColor}15`,
                                    color: barColor,
                                  }}>
                                    {cat.overall_status}
                                  </span>
                                </div>
                                <div className="sr-category-bar">
                                  <div className="sr-category-bar-fill" style={{ width: `${pct}%`, background: barColor }} />
                                </div>
                                <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 8 }}>
                                  {normalCount}/{totalCount} parameters normal
                                </div>
                                {cat.concerns.length > 0 && (
                                  <div className="sr-category-concerns">
                                    {cat.concerns.slice(0, 2).map((c, j) => (
                                      <div key={j} className="sr-category-concern">
                                        <FontAwesomeIcon icon={faExclamationTriangle} /> {c}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Section 5: Diet Recommendations */}
                  {viewingReport.diet_recommendations.length > 0 && (
                    <div className="sr-section-card">
                      <div className="sr-section-header">
                        <div className="sr-section-icon" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
                          <FontAwesomeIcon icon={faAppleWhole} />
                        </div>
                        <span className="sr-section-title">Diet Recommendations</span>
                      </div>
                      <div className="sr-section-body">
                        <div className="sr-diet-grid">
                          {viewingReport.diet_recommendations.map((diet, i) => (
                            <div key={i} className="sr-diet-card">
                              <div className="sr-diet-category">{diet.category}</div>
                              <div className="sr-diet-columns">
                                <div>
                                  <div className="sr-diet-column-title dos">
                                    <FontAwesomeIcon icon={faCheckCircle} /> Include
                                  </div>
                                  <ul className="sr-diet-list dos">
                                    {diet.dos.map((item, j) => <li key={j}>{item}</li>)}
                                  </ul>
                                </div>
                                <div>
                                  <div className="sr-diet-column-title donts">
                                    <FontAwesomeIcon icon={faCircleXmark} /> Avoid
                                  </div>
                                  <ul className="sr-diet-list donts">
                                    {diet.donts.map((item, j) => <li key={j}>{item}</li>)}
                                  </ul>
                                </div>
                              </div>
                              {diet.personalized_note && (
                                <div className="sr-diet-note">{diet.personalized_note}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Section 6: Lifestyle + Specialist Referrals */}
                  {viewingReport.lifestyle_recommendations.length > 0 && (
                    <div className="sr-section-card">
                      <div className="sr-section-header">
                        <div className="sr-section-icon" style={{ background: 'rgba(99, 102, 241, 0.1)', color: '#6366f1' }}>
                          <FontAwesomeIcon icon={faPersonRunning} />
                        </div>
                        <span className="sr-section-title">Lifestyle Recommendations</span>
                      </div>
                      <div className="sr-section-body">
                        <div className="sr-lifestyle-grid">
                          {viewingReport.lifestyle_recommendations.map((rec, i) => (
                            <div key={i} className="sr-lifestyle-card">
                              <div className={`sr-lifestyle-priority ${rec.priority}`}>
                                {rec.priority === 'high' ? <FontAwesomeIcon icon={faArrowUp} /> :
                                 rec.priority === 'medium' ? <FontAwesomeIcon icon={faMinus} /> :
                                 <FontAwesomeIcon icon={faArrowDown} />}
                              </div>
                              <div className="sr-lifestyle-text">
                                <div className="sr-lifestyle-rec">{rec.recommendation}</div>
                                <div className="sr-lifestyle-reason">{rec.reason}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {viewingReport.specialist_referrals.length > 0 && (
                    <div className="sr-section-card">
                      <div className="sr-section-header">
                        <div className="sr-section-icon" style={{ background: 'rgba(236, 72, 153, 0.1)', color: '#ec4899' }}>
                          <FontAwesomeIcon icon={faUserDoctor} />
                        </div>
                        <span className="sr-section-title">Specialist Referrals</span>
                      </div>
                      <div className="sr-section-body">
                        <div className="sr-referrals-grid">
                          {viewingReport.specialist_referrals.map((ref, i) => (
                            <div key={i} className="sr-referral-card">
                              <div className="sr-referral-header">
                                <span className="sr-referral-specialty">{ref.specialty}</span>
                                <span className={`sr-urgency-badge ${ref.urgency}`}>
                                  {ref.urgency}
                                </span>
                              </div>
                              <div className="sr-referral-reason">{ref.reason}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Section 7: Follow-up Tests */}
                  {viewingReport.follow_up_tests.length > 0 && (
                    <div className="sr-section-card">
                      <div className="sr-section-header">
                        <div className="sr-section-icon" style={{ background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}>
                          <FontAwesomeIcon icon={faStethoscope} />
                        </div>
                        <span className="sr-section-title">Recommended Follow-up Tests</span>
                      </div>
                      <div className="sr-section-body">
                        <div className="sr-followup-list">
                          {viewingReport.follow_up_tests.map((test, i) => (
                            <div key={i} className="sr-followup-item">
                              <div className="sr-followup-icon">
                                <FontAwesomeIcon icon={faVial} />
                              </div>
                              <span className="sr-followup-text">{test}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Immediate Actions */}
                  {viewingReport.personalized_summary && viewingReport.personalized_summary.immediate_actions.length > 0 && (
                    <div className="sr-section-card">
                      <div className="sr-section-header">
                        <div className="sr-section-icon" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}>
                          <FontAwesomeIcon icon={faTriangleExclamation} />
                        </div>
                        <span className="sr-section-title">Immediate Actions</span>
                      </div>
                      <div className="sr-section-body">
                        <div className="sr-followup-list">
                          {viewingReport.personalized_summary.immediate_actions.map((action, i) => (
                            <div key={i} className="sr-followup-item">
                              <div className="sr-followup-icon" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}>
                                <FontAwesomeIcon icon={faTriangleExclamation} />
                              </div>
                              <span className="sr-followup-text">{action}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </Container>

      {/* ═══ ADD CREDITS MODAL ═══ */}
      {showCreditsModal && (
        <div className="sr-modal-overlay" onClick={() => !paymentLoading && setShowCreditsModal(false)}>
          <div className="sr-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sr-modal-header">
              <h3 className="sr-modal-title">
                <FontAwesomeIcon icon={faWallet} /> Add Credits
              </h3>
              <button className="sr-modal-close" onClick={() => !paymentLoading && setShowCreditsModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="sr-modal-body">
              <div className="sr-modal-balance">
                Current Balance: <strong>{walletBalance ? walletBalance.balance.toFixed(1) : '0'} credits</strong>
              </div>

              <div className="sr-packs-grid">
                {CREDIT_PACKS.map((pack, idx) => (
                  <div
                    key={idx}
                    className={`sr-pack-card ${selectedPack === idx ? 'selected' : ''}`}
                    onClick={() => handlePackSelect(idx)}
                  >
                    {pack.tag && <span className="sr-pack-tag">{pack.tag}</span>}
                    <div className="sr-pack-icon" style={{ color: pack.color }}>
                      <FontAwesomeIcon icon={pack.icon} />
                    </div>
                    <div className="sr-pack-label">{pack.label}</div>
                    <div className="sr-pack-credits">{pack.credits} Credit{pack.credits > 1 ? 's' : ''}</div>
                    <div className="sr-pack-price">
                      <FontAwesomeIcon icon={faIndianRupeeSign} /> {pack.amount}
                    </div>
                    <div className="sr-pack-per">
                      {pack.credits > 1 ? `₹${Math.round(pack.amount / pack.credits)}/credit` : ''}
                    </div>
                  </div>
                ))}
              </div>

              <div className="sr-custom-amount">
                <label className="sr-custom-label">Or enter custom amount (min ₹49)</label>
                <div className="sr-custom-input-wrap">
                  <span className="sr-custom-prefix">₹</span>
                  <input
                    type="number"
                    className="sr-custom-input"
                    placeholder="Enter amount"
                    min="49"
                    value={customAmount}
                    onChange={(e) => { setCustomAmount(e.target.value); setSelectedPack(null); setPaymentError(null); }}
                  />
                </div>
              </div>

              {paymentError && (
                <div className="sr-payment-error">
                  <FontAwesomeIcon icon={faCircleExclamation} /> {paymentError}
                </div>
              )}
            </div>

            <div className="sr-modal-footer">
              <button
                className="sr-modal-btn secondary"
                onClick={() => setShowCreditsModal(false)}
                disabled={paymentLoading}
              >
                Cancel
              </button>
              <button
                className="sr-modal-btn primary"
                disabled={paymentLoading || (selectedPack === null && (!customAmount || Number(customAmount) < 49))}
                onClick={handleProceedPayment}
              >
                {paymentLoading ? (
                  <><FontAwesomeIcon icon={faSpinner} spin /> Processing...</>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faIndianRupeeSign} />
                    {' '}Pay {selectedPack !== null ? `₹${CREDIT_PACKS[selectedPack].amount}` : customAmount ? `₹${customAmount}` : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden PayU Form */}
      {payuData && (
        <form ref={payuFormRef} method="POST" action={payuBaseUrl} style={{ display: 'none' }}>
          <input type="hidden" name="key" value={payuData.key} />
          <input type="hidden" name="txnid" value={payuData.txnid} />
          <input type="hidden" name="amount" value={payuData.amount} />
          <input type="hidden" name="productinfo" value={payuData.productinfo} />
          <input type="hidden" name="firstname" value={payuData.firstname} />
          <input type="hidden" name="email" value={payuData.email} />
          <input type="hidden" name="phone" value={payuData.phone} />
          <input type="hidden" name="surl" value={payuData.surl} />
          <input type="hidden" name="furl" value={payuData.furl} />
          <input type="hidden" name="hash" value={payuData.hash} />
          <input type="hidden" name="service_provider" value="payu_paisa" />
        </form>
      )}

      {/* In-app toast notification */}
      {toastMsg && (
        <div
          style={{
            position: 'fixed',
            bottom: 32,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10000,
            padding: '12px 24px',
            borderRadius: 12,
            color: '#fff',
            fontWeight: 500,
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
            background: toastMsg.type === 'success' ? '#10b981'
              : toastMsg.type === 'warning' ? '#f59e0b'
              : '#ef4444',
          }}
        >
          <span>{toastMsg.message}</span>
          <button
            onClick={() => setToastMsg(null)}
            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', marginLeft: 8, fontSize: 14 }}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
};

export default SmartReports;
